import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useInvoices, useCustomerOrganisations, useUpdateInvoice, useInvoice } from '../hooks';
import { InvoicesFilters } from '../components/InvoicesFilters';
import { InvoiceStatsBar } from '../components/InvoiceStatsBar';
import { InvoicesTable } from '../components/InvoicesTable';
import { InvoiceFormModal, CreateInvoiceModal } from '../components/modals';
import ReassignDialog from '@/features/accounts/components/ReassignDialog';
import { useSalesManagers } from '@/features/accounts/hooks/useSubscriptionManagers';
import {
  buildSalesManagerFilterOptions,
  parseSalesManagerFilterValue,
} from '@/features/accounts/utils/salesManagerFilters';
import { printInvoicePdf } from '../utils/printInvoice';
import type {
  Invoice,
  InvoicesFilters as InvoicesFiltersType,
  InvoiceStatus,
  UpdateInvoiceInput,
} from '../types';

const COLUMN_FILTERS_STORAGE_KEY = 'atek-invoices-column-filters';

function loadColumnFilters(): Record<string, string> {
  try {
    const stored = JSON.parse(localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY) || '{}');
    const salesManagerId = parseSalesManagerFilterValue(stored.salesManager);
    return {
      ...stored,
      salesManager: salesManagerId || '',
    };
  } catch {
    return {};
  }
}

export default function InvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewInvoiceId = searchParams.get('review');

  // ==========================================================================
  // Server-side filter state
  // ==========================================================================
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [customerId, setCustomerId] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<InvoicesFiltersType['sortKey']>('created_at');
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1);

  // ==========================================================================
  // Column filter state (persisted to localStorage)
  // ==========================================================================
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // ==========================================================================
  // Debounce column filters (text/date/number inputs need debounce before
  // server round-trip; selects apply immediately)
  // ==========================================================================
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<Record<string, string>>(columnFilters);
  const prevColumnFiltersRef = useRef(columnFilters);

  useEffect(() => {
    // Check if the change is a select filter (JSON array) — apply immediately
    const changedKey = Object.keys(columnFilters).find(
      (k) => columnFilters[k] !== prevColumnFiltersRef.current[k],
    );
    prevColumnFiltersRef.current = columnFilters;

    if (changedKey) {
      const val = columnFilters[changedKey] || '';
      if (
        val.startsWith('[')
        || val === ''
        || (changedKey === 'salesManager' && !!parseSalesManagerFilterValue(val))
      ) {
        // Select filter or cleared — apply immediately
        setDebouncedColumnFilters(columnFilters);
        setPage(0);
        return;
      }
    }

    const timer = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnFilters]);

  // ==========================================================================
  // Modal state
  // ==========================================================================
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [pendingInvoiceId, setPendingInvoiceId] = useState<string | null>(null);
  const [reassignInvoice, setReassignInvoice] = useState<Invoice | null>(null);

  // ==========================================================================
  // Debounce toolbar search
  // ==========================================================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // ==========================================================================
  // Map column filters to server-side filter params
  // ==========================================================================
  const columnFilterParams = useMemo((): Partial<InvoicesFiltersType> => {
    const cf = debouncedColumnFilters;
    const params: Partial<InvoicesFiltersType> = {};

    // Text column filters → dedicated server-side ILIKE params
    if (cf.invoiceNumber) params.invoice_number_search = cf.invoiceNumber;
    if (cf.project) params.project_name = cf.project;
    if (cf.contractualManager) params.contractual_manager_search = cf.contractualManager;

    // Searchable-select filters → JSON array of IDs
    if (cf.account) {
      try {
        const ids = JSON.parse(cf.account) as string[];
        if (ids.length) params.account_id_in = ids;
      } catch { /* not JSON — fallback to text search */ params.account_name_search = cf.account; }
    }
    if (cf.salesManager) {
      const salesManagerId = parseSalesManagerFilterValue(cf.salesManager);
      if (salesManagerId) params.sales_manager = salesManagerId;
    }
    if (cf.poNumber) params.po_number = cf.poNumber;
    if (cf.currency) params.currency = cf.currency;
    if (cf.contract) params.contract_search = cf.contract;
    if (cf.nextStep) params.next_step = cf.nextStep;

    // Date range filters → "from>>to" format
    if (cf.date) {
      const [from, to] = cf.date.split('>>');
      if (from) params.date_from = from;
      if (to) params.date_to = to;
    }
    if (cf.expirationDate) {
      const [from, to] = cf.expirationDate.split('>>');
      if (from) params.expiration_date_from = from;
      if (to) params.expiration_date_to = to;
    }
    if (cf.paymentDate) {
      const [from, to] = cf.paymentDate.split('>>');
      if (from) params.payment_date_from = from;
      if (to) params.payment_date_to = to;
    }
    if (cf.createdAt) {
      const [from, to] = cf.createdAt.split('>>');
      if (from) params.created_at_from = from;
      if (to) params.created_at_to = to;
    }
    if (cf.nextStepDeadline) {
      const [from, to] = cf.nextStepDeadline.split('>>');
      if (from) params.next_step_deadline_from = from;
      if (to) params.next_step_deadline_to = to;
    }

    // Number range filters → "min>>max" format
    if (cf.total) {
      const [min, max] = cf.total.split('>>');
      if (min) params.total_min = min;
      if (max) params.total_max = max;
    }

    // Status filter → JSON array
    if (cf.status) {
      try {
        const statuses = JSON.parse(cf.status) as string[];
        if (statuses.length) params.status = statuses as InvoiceStatus[];
      } catch { /* ignore */ }
    }

    // Multi-select filters → JSON array strings
    if (cf.paymentTerms) {
      try {
        const terms = JSON.parse(cf.paymentTerms) as string[];
        if (terms.length) params.payment_terms_in = terms;
      } catch { /* ignore */ }
    }
    if (cf.taxCategory) {
      try {
        const cats = JSON.parse(cf.taxCategory) as string[];
        if (cats.length) params.tax_category_in = cats;
      } catch { /* ignore */ }
    }

    return params;
  }, [debouncedColumnFilters]);

  // ==========================================================================
  // Build filters object (server-side)
  // ==========================================================================
  const filters: InvoicesFiltersType = {
    search: debouncedSearch || undefined,
    customer: customerId !== 'all' ? customerId : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
    ...columnFilterParams,
  };

  // ==========================================================================
  // Queries
  // ==========================================================================
  const invoicesQuery = useInvoices(filters);
  const customersQuery = useCustomerOrganisations();
  const salesManagersQuery = useSalesManagers();

  // Fetch the full invoice before opening the edit modal so Mongo-backed fields stay intact.
  const pendingInvoiceQuery = useInvoice(pendingInvoiceId || undefined);

  useEffect(() => {
    if (reviewInvoiceId) {
      setPendingInvoiceId(reviewInvoiceId);
    }
  }, [reviewInvoiceId]);

  useEffect(() => {
    if (!pendingInvoiceId) return;

    if (pendingInvoiceQuery.data) {
      // Keep pendingInvoiceId live while the modal is open: the detail query then
      // refetches on invalidation (e.g. a PO-file upload/delete) and re-feeds the
      // OPEN modal, instead of leaving it on its opening one-shot snapshot. The id
      // is cleared when the modal closes (onOpenChange), disabling the query.
      setEditingInvoice(pendingInvoiceQuery.data);
    }

    if (pendingInvoiceQuery.error) {
      toast.error('Failed to load invoice');
      setPendingInvoiceId(null);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.delete('review');
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    pendingInvoiceId,
    pendingInvoiceQuery.data,
    pendingInvoiceQuery.error,
    searchParams,
    setSearchParams,
  ]);

  // ==========================================================================
  // Mutations
  // ==========================================================================
  const updateInvoiceMutation = useUpdateInvoice();

  const invoices = invoicesQuery.data?.invoices || [];
  const stats = invoicesQuery.data?.stats || [];
  const total = invoicesQuery.data?.total || 0;
  const customers = customersQuery.data || [];
  const salesManagers = salesManagersQuery.data || [];

  // ==========================================================================
  // Searchable-select dropdown options
  // ==========================================================================
  const searchableSelectOptions = useMemo(() => ({
    account: customers
      .map((c) => ({ label: c.name, value: c._id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    salesManager: buildSalesManagerFilterOptions(salesManagers),
  }), [customers, salesManagers]);

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleSort = (key: InvoicesFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(-1);
    }
    setPage(0);
  };

  const handleCustomerChange = (value: string) => {
    setCustomerId(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleUpdateInvoice = async (data: UpdateInvoiceInput) => {
    if (!editingInvoice) return;
    await updateInvoiceMutation.mutateAsync({
      invoiceId: editingInvoice._id,
      data,
    });
  };

  const handleInvoiceCreated = (invoice: Invoice) => {
    setPendingInvoiceId(invoice._id);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    const mongoInvoiceId = invoice.pgId && invoice._id === invoice.pgId
      ? null
      : invoice._id;

    if (!mongoInvoiceId) {
      toast.error('Invoice is missing its MongoDB link. Wait for sync before editing.');
      return;
    }

    setPendingInvoiceId(mongoInvoiceId);
  };

  const handleViewHtml = (invoiceId: string, fileName: string) => {
    void printInvoicePdf(invoiceId, fileName);
  };

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <nav className="breadcrumb flex items-center gap-1.5">
        <Link to="/erp" className="text-atek-blue hover:text-atek-blue-dark">
          ERP
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-foreground">Invoices</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Invoices</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            Manage and track all customer invoices
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Invoice
        </Button>
      </div>

      {/* Stats Bar */}
      {stats.length > 0 && <InvoiceStatsBar stats={stats} />}

      {/* Server-side filters toolbar */}
      <InvoicesFilters
        search={search}
        onSearchChange={setSearch}
        customerId={customerId}
        onCustomerChange={handleCustomerChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        customers={customers}
        isLoadingCustomers={customersQuery.isLoading}
      />

      {/* Main Content Card */}
      <Card>
        <CardContent className="p-0">
          <InvoicesTable
            invoices={invoices}
            pageItemCount={invoices.length}
            total={total}
            sortKey={sortKey}
            sortDirection={sortDirection}
            onSort={handleSort}
            onEditClick={handleEditInvoice}
            onReassign={setReassignInvoice}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            columnFilters={columnFilters}
            onColumnFilterChange={setColumnFilter}
            onClearColumnFilters={() => setColumnFilters({})}
            isLoading={invoicesQuery.isLoading}
            searchableSelectOptions={searchableSelectOptions}
          />
        </CardContent>
      </Card>

      {/* Create Modal */}
      <CreateInvoiceModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onCreated={handleInvoiceCreated}
      />

      {/* Edit Modal */}
      <InvoiceFormModal
        open={!!editingInvoice}
        onOpenChange={(open) => {
          if (!open) {
            setEditingInvoice(null);
            setPendingInvoiceId(null);
            if (searchParams.has('review')) {
              const nextSearchParams = new URLSearchParams(searchParams);
              nextSearchParams.delete('review');
              setSearchParams(nextSearchParams, { replace: true });
            }
          }
        }}
        invoice={editingInvoice}
        onSubmit={handleUpdateInvoice}
        onViewHtml={handleViewHtml}
        isSubmitting={updateInvoiceMutation.isPending}
      />

      {/* Reassign Dialog */}
      <ReassignDialog
        entityType="invoice"
        entityId={reassignInvoice?.pgId || reassignInvoice?._id || ''}
        entityLabel={reassignInvoice?.invoice_number || ''}
        currentAccountId={
          reassignInvoice?.customer && typeof reassignInvoice.customer === 'object'
            ? reassignInvoice.customer._id
            : ''
        }
        currentAccountName={
          reassignInvoice?.customer && typeof reassignInvoice.customer === 'object'
            ? reassignInvoice.customer.name
            : ''
        }
        open={!!reassignInvoice}
        onOpenChange={(open) => {
          if (!open) {
            setReassignInvoice(null);
            invoicesQuery.refetch();
          }
        }}
      />
    </div>
  );
}
